import { useState, useRef } from 'react'
import { supabase } from '../supabaseClient'
import { parseFoireannPDF } from '../utils/parseFoireannPDF'

const BLUE = '#185FA5'

export default function FoireannImportModal({ squadId, onClose, onImported }) {
  const [step, setStep] = useState('upload')
  const [parsed, setParsed] = useState(null)
  const [players, setPlayers] = useState([])
  const [managers, setManagers] = useState([])
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)
  const fileRef = useRef()

  async function handleFile(e) {
    const file = e.target.files?.[0]
    if (!file) return
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      setError('Please upload a PDF file.')
      return
    }
    setLoading(true)
    setError(null)
    try {
      const result = await parseFoireannPDF(file)
      if (!result.players.length) {
        setError('No players found — make sure this is a Foireann team sheet PDF.')
        setLoading(false)
        return
      }
      setParsed(result)
      setPlayers(result.players)
      setManagers(result.managers)
      setStep('preview')
    } catch (err) {
      setError(err.message || 'Failed to read PDF.')
    }
    setLoading(false)
  }

  function updatePlayer(index, field, value) {
    setPlayers(prev => prev.map((p, i) => i === index ? { ...p, [field]: value } : p))
  }

  function removePlayer(index) {
    setPlayers(prev => prev.filter((_, i) => i !== index))
  }

  function updateManager(index, field, value) {
    setManagers(prev => prev.map((m, i) => i === index ? { ...m, [field]: value } : m))
  }

  function removeManager(index) {
    setManagers(prev => prev.filter((_, i) => i !== index))
  }

  async function handleImport() {
    setLoading(true)
    setError(null)
    try {
    const playerRows = players.map(p => ({
        squad_id: squadId,
        full_name: p.english_name,
        full_name_irish: p.irish_name !== p.english_name ? p.irish_name : null,
        date_of_birth: p.dob || null,
        foireann_id: p.foireann_id || null,
        is_active: true,
        is_guest: false,
      }))

      const { error: playerErr } = await supabase.from('players').insert(playerRows)
      if (playerErr) throw playerErr

      if (managers.length) {
        const managerRows = managers.map(m => ({
          squad_id: squadId,
          full_name: m.english_name,
          full_name_irish: m.irish_name !== m.english_name ? m.irish_name : null,
          foireann_id: m.foireann_id || null,
        }))
        const { error: managerErr } = await supabase.from('squad_managers').insert(managerRows)
        if (managerErr) throw managerErr
      }

    setStep('done')
    } catch (err) {
      setError('Import failed: ' + (err.message || 'Unknown error'))
    }
    setLoading(false)
  }

  const overlay = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
    display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
    zIndex: 1000, overflowY: 'auto', padding: '20px 16px',
  }
  const modal = {
    background: '#fff', borderRadius: 14, width: '100%',
    maxWidth: 700, padding: 24, position: 'relative',
  }

  return (
    <div style={overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={modal}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 20 }}>
          <div style={{ fontSize: 20, fontWeight: 700, color: BLUE }}>Import from Foireann PDF</div>
          <button onClick={() => { onImported?.(); onClose() }} style={{ marginLeft: 'auto', background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#999' }}>×</button>
        </div>

        {error && (
          <div style={{ background: '#fff3f3', border: '1px solid #f5c6cb', borderRadius: 8, padding: '10px 14px', marginBottom: 16, color: '#c0392b', fontSize: 14 }}>
            {error}
          </div>
        )}

        {step === 'upload' && (
          <div>
            <p style={{ color: '#555', fontSize: 14, marginBottom: 20 }}>
              Upload the Foireann team sheet PDF for this squad. Player and manager details will be extracted automatically. The PDF is never stored.
            </p>
            <div
              onClick={() => fileRef.current?.click()}
              style={{
                border: '2px dashed #bbb', borderRadius: 12, padding: '40px 20px',
                textAlign: 'center', cursor: 'pointer', background: '#fafafa',
              }}
            >
              {loading
                ? <div style={{ color: '#888' }}>Reading PDF...</div>
                : <>
                    <div style={{ fontSize: 36, marginBottom: 8 }}>📄</div>
                    <div style={{ fontWeight: 600, color: BLUE, marginBottom: 4 }}>Click to upload Foireann PDF</div>
                    <div style={{ fontSize: 13, color: '#999' }}>PDF files only</div>
                  </>
              }
            </div>
            <input ref={fileRef} type="file" accept=".pdf" style={{ display: 'none' }} onChange={handleFile} />
          </div>
        )}

        {step === 'preview' && (
          <div>
            {parsed?.squadName && (
              <div style={{ background: '#f0f5ff', borderRadius: 8, padding: '8px 14px', marginBottom: 16, fontSize: 14, color: BLUE, fontWeight: 600 }}>
                {parsed.clubName && `${parsed.clubName} — `}{parsed.squadName}
              </div>
            )}

            <div style={{ fontSize: 13, color: '#666', marginBottom: 14 }}>
              Review the extracted data below. Edit or remove any rows before importing.
            </div>

            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 8, color: '#333' }}>
              Players ({players.length})
            </div>
            <div style={{ border: '1px solid #e0e0e0', borderRadius: 10, overflow: 'hidden', marginBottom: 20 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#f5f7fa' }}>
                    <th style={th}>English Name</th>
                    <th style={th}>Irish Name</th>
                    <th style={th}>DOB</th>
                    <th style={th}>Foireann ID</th>
                    <th style={th}></th>
                  </tr>
                </thead>
                <tbody>
                  {players.map((p, i) => (
                    <tr key={i} style={{ borderTop: '1px solid #f0f0f0' }}>
                      <td style={td}><EditCell value={p.english_name} onChange={v => updatePlayer(i, 'english_name', v)} /></td>
                      <td style={td}><EditCell value={p.irish_name} onChange={v => updatePlayer(i, 'irish_name', v)} /></td>
                      <td style={td}><EditCell value={p.dob} onChange={v => updatePlayer(i, 'dob', v)} /></td>
                      <td style={td}><EditCell value={p.foireann_id} onChange={v => updatePlayer(i, 'foireann_id', v)} /></td>
                      <td style={{ ...td, textAlign: 'center' }}>
                        <button onClick={() => removePlayer(i)} style={{ background: 'none', border: 'none', color: '#e74c3c', cursor: 'pointer', fontSize: 16 }}>×</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {managers.length > 0 && <>
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 8, color: '#333' }}>
                Managers ({managers.length})
              </div>
              <div style={{ border: '1px solid #e0e0e0', borderRadius: 10, overflow: 'hidden', marginBottom: 20 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: '#f5f7fa' }}>
                      <th style={th}>English Name</th>
                      <th style={th}>Irish Name</th>
                      <th style={th}>Foireann ID</th>
                      <th style={th}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {managers.map((m, i) => (
                      <tr key={i} style={{ borderTop: '1px solid #f0f0f0' }}>
                        <td style={td}><EditCell value={m.english_name} onChange={v => updateManager(i, 'english_name', v)} /></td>
                        <td style={td}><EditCell value={m.irish_name} onChange={v => updateManager(i, 'irish_name', v)} /></td>
                        <td style={td}><EditCell value={m.foireann_id} onChange={v => updateManager(i, 'foireann_id', v)} /></td>
                        <td style={{ ...td, textAlign: 'center' }}>
                          <button onClick={() => removeManager(i)} style={{ background: 'none', border: 'none', color: '#e74c3c', cursor: 'pointer', fontSize: 16 }}>×</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>}

            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={() => setStep('upload')}
                style={{ flex: 1, padding: '12px 0', background: '#f0f0f0', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 600, fontSize: 15 }}
              >
                ← Back
              </button>
              <button
                onClick={handleImport}
                disabled={loading || !players.length}
                style={{ flex: 2, padding: '12px 0', background: BLUE, color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 700, fontSize: 15, opacity: loading ? 0.6 : 1 }}
              >
                {loading ? 'Importing...' : `Import ${players.length} player${players.length !== 1 ? 's' : ''}${managers.length ? ` + ${managers.length} manager${managers.length !== 1 ? 's' : ''}` : ''}`}
              </button>
            </div>
          </div>
        )}

        {step === 'done' && (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#27ae60', marginBottom: 8 }}>Import complete!</div>
            <div style={{ fontSize: 14, color: '#666', marginBottom: 24 }}>
              {players.length} players{managers.length ? ` and ${managers.length} managers` : ''} added to the squad.
            </div>
            <button
              onClick={() => { onImported?.(); onClose() }}
              style={{ padding: '12px 32px', background: BLUE, color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 700, fontSize: 15 }}
            >
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

function EditCell({ value, onChange }) {
  return (
    <input
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      style={{ width: '100%', border: 'none', background: 'transparent', fontSize: 13, padding: '2px 0', outline: 'none' }}
    />
  )
}

const th = { padding: '9px 12px', textAlign: 'left', fontWeight: 600, color: '#555', fontSize: 12 }
const td = { padding: '8px 12px' }